"use client"

import { Component } from "react"

import type { ErrorInfo, ReactNode } from "react"

import { Button } from "@/components/ui/button"
import { DialogPortal } from "@/components/ui/dialog"

import { modalTexts } from "./localization"

type ModalLoadBoundaryProps = {
  onRetry: () => void
  onClose: () => void
  children: ReactNode
}

type ModalLoadBoundaryState = {
  failed: boolean
}

// React requires a class here (AGENTS.md §6 allows error boundaries).
// use() rethrows a rejected chunk import straight into render, so without this
// a stale deploy (404 on the hashed chunk) would unmount the whole app.
export class ModalLoadBoundary extends Component<
  ModalLoadBoundaryProps,
  ModalLoadBoundaryState
> {
  state: ModalLoadBoundaryState = { failed: false }

  static getDerivedStateFromError(): ModalLoadBoundaryState {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[modal] content failed to load", error, info.componentStack)
  }

  // Children were unmounted with the fallback, so clearing the flag remounts
  // them and re-runs loadModal against the freshly invalidated cache.
  handleRetry = () => {
    this.props.onRetry()
    this.setState({ failed: false })
  }

  render() {
    if (!this.state.failed) return this.props.children

    // Needs its own dismiss: no Dialog.Content is mounted in this state, so
    // Radix handles neither Escape nor a click on the overlay, and the
    // backdrop would otherwise be unclosable while the chunk keeps failing.
    return (
      <DialogPortal>
        <div className="fixed inset-0 z-1200 flex items-center justify-center p-4">
          <div
            role="alert"
            className="flex flex-col items-center gap-4 rounded-lg bg-black-800 p-6 text-center text-white"
          >
            <p className="text-sm text-white/80">{modalTexts.loadError}</p>
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={this.handleRetry}>
                {modalTexts.retry}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => this.props.onClose()}
              >
                {modalTexts.close}
              </Button>
            </div>
          </div>
        </div>
      </DialogPortal>
    )
  }
}
